import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import {addBreadcrumb} from '@sentry/browser';
import {useLocation, useNavigate} from 'react-router';

import logError from '@/utils/errors/logError';

import {
  CO_PARENT_INVITATION_STORAGE_KEY,
  CO_PARENT_QUERY_PARAMS,
  CO_PARENT_SIGN_UP_PATH,
} from './constants';
import {CoParentInvitation} from './interfaces';
import {useTrackers} from './trackers';

interface CoparentViewModelContextValue {
  invitation: CoParentInvitation;
  inviterName: string;
  kids: string[];
  handleAcceptInvite: () => void;
}

const CoparentViewModelContext =
  createContext<CoparentViewModelContextValue | null>(null);

const buildInvitation = (search: string): CoParentInvitation => {
  const params = new URLSearchParams(search);
  const rawParams = Object.fromEntries(params.entries());

  const kids = params
    .get(CO_PARENT_QUERY_PARAMS.KIDS)
    ?.split(',')
    .map(kid => kid.trim())
    .filter(Boolean);

  return {
    inviterName: params.get(CO_PARENT_QUERY_PARAMS.INVITER_NAME) ?? undefined,
    kids: kids?.length ? kids : undefined,
    email: params.get(CO_PARENT_QUERY_PARAMS.EMAIL) ?? undefined,
    firstName:
      params.get(CO_PARENT_QUERY_PARAMS.FIRST_NAME) ??
      params.get(CO_PARENT_QUERY_PARAMS.INVITE_NAME) ??
      undefined,
    lastName: params.get(CO_PARENT_QUERY_PARAMS.LAST_NAME) ?? undefined,
    phone: params.get(CO_PARENT_QUERY_PARAMS.PHONE) ?? undefined,
    rawParams,
  };
};

export function CoparentViewModelProvider({children}: {children: ReactNode}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {trackViewCoParentingLinkOpen, trackSelectCoParentingAcceptInvite} =
    useTrackers();

  // Derive the invitation synchronously so the page renders with the real data
  // on first paint, avoiding a flash of default content.
  const invitation = useMemo(
    () => buildInvitation(location.search),
    [location.search],
  );

  // Persist the params so the sign-up form can be pre-filled after the user
  // accepts the invite.
  useEffect(() => {
    addBreadcrumb({
      level: 'info',
      category: 'coParent.invitation.mount',
      type: 'info',
      message: 'Co-parent invitation opened',
    });

    trackViewCoParentingLinkOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        CO_PARENT_INVITATION_STORAGE_KEY,
        JSON.stringify(invitation),
      );
    } catch (error) {
      logError(error, {
        fileName: 'CoparentViewModelContext.tsx',
        functionName: 'persistInvitation',
        customTitle: 'CoParent - Invitation - failed to persist invitation',
        extraTags: {
          userFlow: 'CoParent',
          screen: 'Invitation',
        },
      });
    }
  }, [invitation]);

  const handleAcceptInvite = useCallback(() => {
    addBreadcrumb({
      level: 'info',
      category: 'coParent.invitation.acceptInvite',
      type: 'user',
      message: 'Co-parent invitation accepted',
    });
    trackSelectCoParentingAcceptInvite();
    navigate(CO_PARENT_SIGN_UP_PATH);
  }, [navigate, trackSelectCoParentingAcceptInvite]);

  const value = useMemo<CoparentViewModelContextValue>(
    () => ({
      invitation,
      inviterName: invitation.inviterName ?? '',
      kids: invitation.kids ?? [],
      handleAcceptInvite,
    }),
    [invitation, handleAcceptInvite],
  );

  return (
    <CoparentViewModelContext.Provider value={value}>
      {children}
    </CoparentViewModelContext.Provider>
  );
}

export function useCoparentViewModelProvider() {
  const context = useContext(CoparentViewModelContext);
  if (!context) {
    throw new Error(
      'useCoparentViewModelProvider must be used within a CoparentViewModelProvider',
    );
  }
  return context;
}
